import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { gradYearFromGrade } from '@/lib/gradYear';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { Player, ScheduleEvent, ContactSubmission } from '@/types';
import {
  Users,
  UserPlus,
  Calendar,
  TrendingUp,
  Plus,
  BarChart3,
  Shield,
  Inbox,
  Newspaper,
  Award,
  Video,
} from 'lucide-react';
import { EditPlayerModal } from '@/components/modals/EditPlayerModal';
import { RosterTable } from '@/components/coach/RosterTable';
import { RequestsTab } from '@/components/coach/RequestsTab';
import { NewsTab } from '@/components/coach/NewsTab';
import { StaffCard } from '@/components/coach/StaffCard';
import { SyncHealthCard } from '@/components/coach/SyncHealthCard';
import { OnboardingTab, findLinkedPlayer, type SignupUser } from '@/components/coach/OnboardingTab';
import { HonorsTab } from '@/components/coach/HonorsTab';

type DashboardTab = 'overview' | 'roster' | 'onboarding' | 'requests' | 'news' | 'honors' | 'schedule';

export const CoachDashboard: React.FC = () => {
  const { userRole } = useAuth();
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [players, setPlayers] = useState<Player[]>([]);
  const [signups, setSignups] = useState<SignupUser[]>([]);
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<ScheduleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [teamStats, setTeamStats] = useState({
    totalPlayers: 0,
    teamAverage: 0,
    totalGames: 0,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    // Each section catches its own errors so one bad query never blanks the dashboard
    try {
      // Fetch ALL tracked players (active and inactive; the roster tab shows everyone)
      const playersRef = collection(db, 'players');
      const playersQuery = query(
        playersRef,
        where('programId', '==', 'willard-tigers')
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Player[];
      setPlayers(playersData);

      // Overview tiles count the actual team: Willard, not removed, active
      const activePlayers = playersData.filter(
        p => p.isActive && p.willardTeam !== false && p.removed !== true
      );
      const totalPlayers = activePlayers.length;
      const totalGames = activePlayers.reduce((sum, p) => sum + (p.gamesPlayed || 0), 0);
      const averages = activePlayers.filter(p => p.averageScore > 0).map(p => p.averageScore);
      const teamAverage = averages.length > 0
        ? Math.round(averages.reduce((sum, avg) => sum + avg, 0) / averages.length)
        : 0;

      setTeamStats({ totalPlayers, teamAverage, totalGames });
    } catch (error) {
      console.error('Error fetching players:', error);
    }

    try {
      // Fetch upcoming events
      const eventsRef = collection(db, 'schedules');
      const eventsQuery = query(
        eventsRef,
        where('programId', '==', 'willard-tigers')
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as ScheduleEvent[];

      const upcoming = eventsData
        .filter(e => e.date >= new Date())
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .slice(0, 5);
      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error('Error fetching events:', error);
    }

    try {
      // Fetch player-portal signups (users docs, role 'player') for the onboarding queue
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const signupData = usersSnapshot.docs
        .filter(d => d.data().role === 'player')
        .map(d => ({
          uid: d.id,
          email: d.data().email || '',
          displayName: d.data().displayName || '',
          usbcId: d.data().usbcId || undefined,
          createdAt: d.data().createdAt?.toDate() || new Date(),
        })) as SignupUser[];
      signupData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setSignups(signupData);
    } catch (error) {
      console.error('Error fetching signups:', error);
    }

    try {
      // Fetch contact submissions, newest first
      const subsSnapshot = await getDocs(collection(db, 'contactSubmissions'));
      const subsData = subsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
      })) as ContactSubmission[];
      subsData.sort((a, b) => b.submittedAt.getTime() - a.submittedAt.getTime());
      setSubmissions(subsData);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    }

    setLoading(false);
  };

  const handleTogglePlayerStatus = async (playerId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'players', playerId), {
        isActive: !currentStatus,
        updatedAt: serverTimestamp(),
      });
      await fetchDashboardData();
    } catch (error) {
      console.error('Error toggling player status:', error);
    }
  };

  const handleToggleFlag = async (player: Player, field: 'published' | 'consentOnFile') => {
    const next = !player[field];
    // Publishing requires a signed consent form on file (UI also blocks this)
    if (field === 'published' && next && !player.consentOnFile) return;

    const prevPlayers = players;
    setPlayers(list =>
      list.map(p => (p.id === player.id ? { ...p, [field]: next } : p))
    );
    try {
      await updateDoc(doc(db, 'players', player.id), {
        [field]: next,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(`Error toggling ${field}:`, error);
      setPlayers(prevPlayers);
    }
  };

  const handleRemovePlayer = async (player: Player) => {
    if (!window.confirm(`Remove ${player.name} from the program? They disappear from the site and stop syncing. You can restore them later (check "Show all tracked bowlers").`)) return;
    const prevPlayers = players;
    setPlayers(list => list.map(p => (p.id === player.id ? { ...p, removed: true, published: false } : p)));
    try {
      await updateDoc(doc(db, 'players', player.id), {
        removed: true,
        published: false,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error removing player:', error);
      setPlayers(prevPlayers);
    }
  };

  const handleRestorePlayer = async (player: Player) => {
    const prevPlayers = players;
    setPlayers(list => list.map(p => (p.id === player.id ? { ...p, removed: false } : p)));
    try {
      await updateDoc(doc(db, 'players', player.id), {
        removed: false,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error restoring player:', error);
      setPlayers(prevPlayers);
    }
  };

  // Permanent delete for kids who graduated or left: removes the player doc AND
  // their leagueStats/awards rows. Only offered on already-removed (tombstoned)
  // rows. The website owns the roster — the next push-roster run also takes
  // them off the Stats app's team doc (their scoring history stays there).
  const handleHardDeletePlayer = async (player: Player) => {
    if (
      !window.confirm(
        `PERMANENTLY delete ${player.name}?\n\nThis erases their player card, league history, and awards from the site. It cannot be undone.\n\nThe next roster sync also removes them from the Stats app's team roster (their game history stays in the scoring tool).`
      )
    )
      return;
    try {
      for (const coll of ['leagueStats', 'awards'] as const) {
        const rows = await getDocs(query(collection(db, coll), where('playerId', '==', player.id)));
        await Promise.all(rows.docs.map(d => deleteDoc(d.ref)));
      }
      await deleteDoc(doc(db, 'players', player.id));
      setPlayers(list => list.filter(p => p.id !== player.id));
    } catch (error) {
      console.error('Error hard-deleting player:', error);
      alert('Delete failed, check console.');
    }
  };

  const handleSubmissionStatus = async (
    submissionId: string,
    status: ContactSubmission['status']
  ) => {
    const prevSubmissions = submissions;
    setSubmissions(list =>
      list.map(s => (s.id === submissionId ? { ...s, status } : s))
    );
    try {
      await updateDoc(doc(db, 'contactSubmissions', submissionId), { status });
    } catch (error) {
      console.error('Error updating submission status:', error);
      setSubmissions(prevSubmissions);
    }
  };

  const handleEditPlayer = (player: Player) => {
    if (userRole !== 'coach') {
      alert('Only coaches can edit player profiles');
      return;
    }
    setEditingPlayer(player);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingPlayer(null);
  };

  const handleSavePlayer = async () => {
    await fetchDashboardData();
  };

  // One-click onboarding from a join-team request: the card is created with the
  // email as the claim key, so when the kid signs up with that same email their
  // account links automatically. The USBC # feeds the weekly scrape.
  const handleCreateFromRequest = async (submission: ContactSubmission) => {
    try {
      const gradYear = submission.grade ? gradYearFromGrade(submission.grade) : null;
      await addDoc(collection(db, 'players'), {
        name: submission.name,
        email: submission.email,
        ...(submission.usbcId ? { usbcId: submission.usbcId } : {}),
        ...(submission.grade ? { grade: submission.grade } : { grade: '' }),
        ...(gradYear ? { graduationYear: gradYear } : {}),
        programId: 'willard-tigers',
        teamIds: [],
        averageScore: 0,
        highGame: 0,
        gamesPlayed: 0,
        isActive: true,
        willardTeam: true,
        published: false,
        consentOnFile: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'contactSubmissions', submission.id), { status: 'read' });
      await fetchDashboardData();
    } catch (error) {
      console.error('Error creating player from request:', error);
      alert('Could not create the player card. Please try again.');
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    const prevSubmissions = submissions;
    setSubmissions(list => list.filter(s => s.id !== submissionId));
    try {
      await deleteDoc(doc(db, 'contactSubmissions', submissionId));
    } catch (error) {
      console.error('Error deleting submission:', error);
      setSubmissions(prevSubmissions);
    }
  };

  const newSubmissionCount = submissions.filter(s => s.status === 'new').length;
  const pendingSignupCount = signups.filter(u => !findLinkedPlayer(u, players)).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-tiger-neutral-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiger-primary-black"></div>
      </div>
    );
  }

  // Permission check: Only coaches can access this dashboard
  if (userRole !== 'coach') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-tiger-neutral-50 to-white flex items-center justify-center">
        <div className="max-w-md text-center p-8">
          <Shield className="w-16 h-16 text-tiger-neutral-400 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-black text-tiger-primary-black mb-2">
            Access Restricted
          </h1>
          <p className="text-tiger-neutral-600">
            Only coaches can access this dashboard. Please contact your coach if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-tiger-neutral-50 to-white">
      {/* Header */}
      <section className="bg-tiger-primary-black text-white py-12">
        <div className="w-full max-w-[95%] 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-display font-black mb-2">
            Coach Dashboard
          </h1>
          <p className="text-tiger-neutral-300">
            Manage your team, track progress, and view analytics
          </p>
        </div>
      </section>

      <div className="w-full max-w-[95%] 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'overview'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('roster')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'roster'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Roster Management
          </button>
          <button
            onClick={() => setActiveTab('onboarding')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'onboarding'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Onboarding
            {pendingSignupCount > 0 && (
              <span
                className={`ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-bold ${
                  activeTab === 'onboarding'
                    ? 'bg-white text-tiger-primary-black'
                    : 'bg-tiger-primary-black text-white'
                }`}
              >
                {pendingSignupCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'requests'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <Inbox className="w-4 h-4 inline mr-2" />
            Requests
            {newSubmissionCount > 0 && (
              <span
                className={`ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-bold ${
                  activeTab === 'requests'
                    ? 'bg-white text-tiger-primary-black'
                    : 'bg-tiger-primary-black text-white'
                }`}
              >
                {newSubmissionCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'news'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <Newspaper className="w-4 h-4 inline mr-2" />
            News
          </button>
          <button
            onClick={() => setActiveTab('honors')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'honors'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <Award className="w-4 h-4 inline mr-2" />
            Honors
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'schedule'
                ? 'bg-tiger-primary-black text-white shadow-tiger'
                : 'bg-white text-tiger-neutral-700 hover:bg-tiger-neutral-100'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Schedule
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <SyncHealthCard />
            <StaffCard />
            {/* Team Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <StatCard
                icon={Users}
                label="Active Players"
                value={teamStats.totalPlayers.toString()}
                color="from-blue-600 to-blue-800"
              />
              <StatCard
                icon={TrendingUp}
                label="Team Average"
                value={teamStats.teamAverage.toString()}
                color="from-tiger-tiger-darkRed to-red-700"
              />
              <StatCard
                icon={BarChart3}
                label="Total Games"
                value={teamStats.totalGames.toString()}
                color="from-tiger-neutral-700 to-tiger-neutral-900"
              />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-tiger-lg p-6">
              <h2 className="text-xl font-display font-black text-tiger-primary-black mb-4">
                Quick Actions
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => setActiveTab('roster')}
                  className="flex items-center justify-center px-6 py-4 bg-tiger-primary-black text-white font-semibold rounded-lg hover:bg-tiger-neutral-800 transition-colors"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Add Player
                </button>
                <a
                  href={import.meta.env.VITE_STATS_APP_URL || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-6 py-4 bg-tiger-tiger-darkRed text-white font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  <BarChart3 className="w-5 h-5 mr-2" />
                  View Advanced Stats
                </a>
                <a
                  href="https://bowlingstatstracker.web.app/lanelab.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-6 py-4 bg-tiger-neutral-800 text-white font-semibold rounded-lg hover:bg-tiger-neutral-700 transition-colors"
                >
                  <Video className="w-5 h-5 mr-2" />
                  LaneLab Film Room
                </a>
                <button
                  onClick={() => setActiveTab('schedule')}
                  className="flex items-center justify-center px-6 py-4 bg-tiger-tiger-gold text-tiger-primary-black font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Manage Schedule
                </button>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-2xl shadow-tiger-lg p-6">
              <h2 className="text-xl font-display font-black text-tiger-primary-black mb-4">
                Upcoming Events
              </h2>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.map(event => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-4 bg-tiger-neutral-50 rounded-lg hover:bg-tiger-neutral-100 transition-colors"
                    >
                      <div>
                        <h3 className="font-semibold text-tiger-primary-black">
                          {event.title}
                        </h3>
                        <p className="text-sm text-tiger-neutral-600">
                          {event.date.toLocaleDateString()} at {event.time}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        event.type === 'tournament'
                          ? 'bg-tiger-tiger-gold text-tiger-primary-black'
                          : event.type === 'match'
                          ? 'bg-tiger-tiger-darkRed text-white'
                          : 'bg-tiger-neutral-300 text-tiger-neutral-800'
                      }`}>
                        {event.type}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-tiger-neutral-600 text-center py-8">
                  No upcoming events scheduled
                </p>
              )}
            </div>
          </div>
        )}

        {/* Roster Management Tab */}
        {activeTab === 'roster' && (
          <RosterTable
            players={players}
            onEdit={handleEditPlayer}
            onToggleActive={handleTogglePlayerStatus}
            onToggleFlag={handleToggleFlag}
            onRemove={handleRemovePlayer}
            onRestore={handleRestorePlayer}
            onHardDelete={handleHardDeletePlayer}
          />
        )}

        {/* Onboarding Tab */}
        {activeTab === 'onboarding' && (
          <OnboardingTab
            signups={signups}
            players={players}
            onEditPlayer={handleEditPlayer}
            onRefresh={fetchDashboardData}
          />
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <RequestsTab
            submissions={submissions}
            players={players}
            onStatusChange={handleSubmissionStatus}
            onDelete={handleDeleteSubmission}
            onCreatePlayer={handleCreateFromRequest}
          />
        )}

        {/* News Tab */}
        {activeTab === 'news' && <NewsTab />}

        {/* Honors Tab */}
        {activeTab === 'honors' && <HonorsTab players={players} />}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="bg-white rounded-2xl shadow-tiger-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display font-black text-tiger-primary-black">
                Schedule Management
              </h2>
              <button className="px-4 py-2 bg-tiger-primary-black text-white font-semibold rounded-lg hover:bg-tiger-neutral-800 transition-colors flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </button>
            </div>
            <p className="text-tiger-neutral-600 text-center py-12">
              Schedule management interface - Navigate to the public Schedule page to view all events
            </p>
          </div>
        )}
      </div>

      {/* Edit Player Modal */}
      {editingPlayer && (
        <EditPlayerModal
          player={editingPlayer}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSavePlayer}
        />
      )}
    </div>
  );
};

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color }) => {
  return (
    <div className="bg-white rounded-xl shadow-tiger-lg p-6">
      <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${color} mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="text-3xl font-display font-black text-tiger-primary-black mb-1">
        {value}
      </div>
      <div className="text-sm font-semibold text-tiger-neutral-600">
        {label}
      </div>
    </div>
  );
};
